import { Text } from '@/components/DesignSystem';
import { ApiRetailer, PointsOverviewPeriod } from '@api/interfaces';
import { Box, useTheme } from '@mui/material';
import { formatNumber, formatShortNumber } from '@shared/utilities';
import { EChartsOption, SeriesOption } from 'echarts';
import ReactECharts from 'echarts-for-react';
import i18next from 'i18next';
import { round } from 'lodash';
import { DateTime } from 'luxon';
import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface PointsOverviewChartProps {
  periods: PointsOverviewPeriod[],
  retailer?: ApiRetailer,
}

export const PointsOverviewChart = ({
  periods,
  retailer,
}: PointsOverviewChartProps) => {
  const { t, i18n } = useTranslation();

  // Parse API month label "Jun '24" (always en-US) and reformat in the current locale.
  const localizeLabel = (label: string): string => {
    if (!label) {
      return label;
    }
    const match = label.match(/^([A-Za-z]+) '(\d{2})$/);
    if (!match) {
      return label;
    }
    const [, monthAbbr, twoDigitYear] = match;
    const dt = DateTime.fromFormat(
      `${monthAbbr} 20${twoDigitYear}`,
      'MMM yyyy',
      { locale: 'en-US' },
    );
    if (!dt.isValid) {
      return label;
    }
    const localized = dt.setLocale(i18n.language);
    return `${localized.toFormat('MMM')} '${localized.toFormat('yy')}`;
  };

  const theme = useTheme();
  const chartRef = useRef<ReactECharts>(null);
  const cumulativeSumPeriods = useMemo(() => {
    let issuedSum = 0;
    let redeemedSum = 0;
    let reservedSum = 0;
    return periods.map((period => ({
      label: period.label,
      pointsIssued: period.pointsIssued === null
        ? null
        : issuedSum += period.pointsIssued,
      pointsRedeemed: period.pointsRedeemed === null
        ? null
        : redeemedSum += period.pointsRedeemed,
      pointsReserved: period.pointsReserved === null
        ? null
        : reservedSum += period.pointsReserved,
    })));
  }, [periods]);

  const xLabels = ['', ...cumulativeSumPeriods.map(({ label }) => label), ''];
  const localizedXLabels = xLabels.map(localizeLabel);
  const issuedSeries = cumulativeSumPeriods
    .map(({ pointsIssued }) => pointsIssued === null ? null : round(pointsIssued, 2));
  const issuedSeriesForChart = [null, ...issuedSeries, null];
  const redeemedSeries = cumulativeSumPeriods
    .map(({ pointsRedeemed }) => pointsRedeemed === null ? null : round(pointsRedeemed, 2));
  const redeemedSeriesForChart = [null, ...redeemedSeries, null];
  const reservedSeries = cumulativeSumPeriods
    .map(({ pointsReserved }) => pointsReserved === null ? null : round(pointsReserved, 2));
  const reservedSeriesForChart = [null, ...reservedSeries, null];

  const isDataEmpty = !issuedSeries.length && !redeemedSeries.length && !reservedSeries.length;
  const hasBookings = !!retailer?.hasBookings;
  const isNoData = !!periods.length && !(
    issuedSeries.some(v => v && v > 0)
    || redeemedSeries.some(v => v && v > 0)
    || (hasBookings && reservedSeries.some(v => v && v > 0))
  );
  const maxYValue = isDataEmpty ? 1 : Math.max(
    ...(issuedSeries.filter(v => v !== null)) as number[],
    ...(redeemedSeries.filter(v => v !== null)) as number[],
    ...(hasBookings ? reservedSeries.filter(v => v !== null) : []) as number[],
  );
  const maxYAxis = isDataEmpty ? 1 : maxYValue * 1.2;

  const nameTextStyle = {
    fontFamily: 'IBM Plex Sans Condensed',
    fontSize: '14px',
    fontWeight: 400,
    color: theme.palette.text.primary,
  };

  useEffect(() => {
    if (!chartRef.current) {
      return;
    }

    const series: SeriesOption[] = [
      {
        data: issuedSeriesForChart,
        itemStyle: { color: '#884d88' },
        lineStyle: { color: '#884d88', width: 3 },
        name: t($ => $.overview.chart.pointsIssued),
        smooth: false,
        symbolSize: 8,
        type: 'line',
      },
      {
        data: redeemedSeriesForChart,
        itemStyle: { color: '#4482af' },
        lineStyle: { color: '#4482af', width: 3 },
        name: t($ => $.overview.chart.pointsRedeemed),
        smooth: false,
        symbolSize: 8,
        type: 'line',
      },
    ];

    if (hasBookings) {
      series.push({
        data: reservedSeriesForChart,
        itemStyle: { color: '#06BAD1' },
        lineStyle: { color: '#06BAD1', width: 3 },
        name: t($ => $.overview.chart.pointsReserved),
        smooth: false,
        symbolSize: 8,
        type: 'line',
      });
    }

    const newOptions: EChartsOption = {
      animationDuration: 1000,
      grid: { top: 24, right: 8, bottom: 42, left: 58, containLabel: false },
      legend: {
        align: 'left',
        data: [
          { name: t($ => $.overview.chart.pointsIssued), icon: 'roundRect' },
          { name: t($ => $.overview.chart.pointsRedeemed), icon: 'roundRect' },
          ...(hasBookings
            ? [{ name: t($ => $.overview.chart.pointsReserved), icon: 'roundRect' }]
            : []
          ),
        ],
        itemGap: 30,
        itemHeight: 10,
        itemWidth: 10,
        padding: 0,
        right: 20,
        selected: {
          [t($ => $.overview.chart.pointsIssued)]: !isNoData,
          [t($ => $.overview.chart.pointsRedeemed)]:
            !isNoData && redeemedSeriesForChart.some((v => v !== null)),
          ...(hasBookings ? {
            [t($ => $.overview.chart.pointsReserved)]:
              !isNoData && reservedSeriesForChart.some((v => v !== null)),
          } : {}),
        },
        textStyle: {
          lineHeight: 16,
          rich: {
            label: { align: 'left', lineHeight: 24 },
          },
        },
      },
      series,
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const points: any[] = Array.isArray(params) ? params : [params];
          const axisLabel = points[0]?.axisValue ?? '';
          const lines = points
            .filter((p) => p.data !== null && p.data !== undefined)
            .map((p) => `${p.marker}${p.seriesName}: <b>${formatNumber(p.data, i18next)}</b>`);
          return [axisLabel, ...lines].filter(Boolean).join('<br/>');
        },
      },
      xAxis: {
        axisLabel: {
          fontFamily: 'IBM Plex Sans Condensed',
          fontSize: '12px',
          fontWeight: 400,
          formatter: (value: string) => value || '',
        },
        axisTick: { show: false },
        boundaryGap: false,
        data: localizedXLabels,
        name: t($ => $.common.table.date),
        nameGap: 30,
        nameLocation: 'middle',
        nameTextStyle,
        splitLine: { interval: 0, show: true },
        type: 'category',
      },
      yAxis: {
        axisLabel: {
          fontFamily: 'IBM Plex Sans Condensed',
          fontSize: '12px',
          fontWeight: 400,
          formatter: (value: number) => {
            if (isDataEmpty || (value > maxYAxis * 0.99 && value < maxYAxis * 1.01)) {
              return '    ';
            }
            return formatShortNumber(value, i18next);
          },
        },
        max: maxYAxis,
        min: 0,
        name: t($ => $.overview.chart.pointsAxis),
        nameGap: 42,
        nameLocation: 'middle',
        nameTextStyle,
        type: 'value',
      },
    };
    const instance = chartRef.current.getEchartsInstance();
    instance.setOption(newOptions, false, true);
    instance.resize();
  }, [
    hasBookings,
    i18n.language,
    isDataEmpty,
    isNoData,
    issuedSeriesForChart,
    localizedXLabels,
    maxYAxis,
    nameTextStyle,
    redeemedSeriesForChart,
    reservedSeriesForChart,
  ]);

  const emptyOptions = useMemo(() => ({}), []);
  const MemoizedChart = useMemo(() => (
    <ReactECharts
      lazyUpdate={true}
      notMerge={true}
      option={emptyOptions}
      ref={chartRef}
      style={{ height: '428px', width: '100%' }}
    />
  ), [chartRef, emptyOptions]);

  return (
    <Box height="500px" position="relative" width="100%">
      {isNoData && (
        <Box
          alignItems="center"
          bgcolor="rgba(255,255,255,0.7)"
          bottom={0}
          display="flex"
          justifyContent="center"
          left={0}
          position="absolute"
          right={0}
          top={0}
          zIndex={10}
        >
          <Box
            bgcolor="white"
            borderRadius="8px"
            boxShadow={3}
            padding="12px 16px"
          >
            <Text category="body-large">
              {t($ => $.common.error.noDataAvailable)}
            </Text>
          </Box>
        </Box>
      )}
      {MemoizedChart}
    </Box>
  );
};
